'use client'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useCartStore } from '@/stores/cartStore'
import { IS_PRODUCTION } from '@/utils/siteUrl'
import { createClient } from '@/utils/supabase/client'
import type { User } from '@supabase/supabase-js'
import { LogOut, Menu, ShoppingCart, User as UserIcon, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

interface HeaderClientProps {
  user: User | null
  isAdmin?: boolean
}

export function HeaderClient({ user, isAdmin }: HeaderClientProps) {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const { itemCount, refreshCount } = useCartStore(
    useShallow((state) => ({
      itemCount: state.itemCount,
      refreshCount: state.refreshCount,
    })),
  )

  // Initialize cart count on component mount
  useEffect(() => {
    if (user) {
      refreshCount()
    }
  }, [user, refreshCount])

  const handleSignOut = async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    router.push('/auth')
  }

  const userNavigation = [
    { name: 'Microsites', href: '/microsites', adminOnly: false },
    { name: 'Orders', href: '/orders', adminOnly: false },
    { name: 'Users', href: '/users', adminOnly: true },
    { name: 'Products', href: '/products', adminOnly: true },
  ]

  return (
    <header className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center space-x-8">
            <div className="flex-shrink-0">
              <Link href={'/'} className="flex items-center">
                <Image
                  src="/assets/images/logo.png"
                  alt="OneScanReach"
                  priority
                  width={198.02}
                  height={40}
                />
                {IS_PRODUCTION ? null : (
                  <div className="flex items-center space-x-8 ml-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                    Staging
                  </div>
                )}
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8 items-center">
              {user ? (
                <>
                  {userNavigation
                    .filter((item) => !item.adminOnly || isAdmin)
                    .map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className={cn(
                          'text-sm font-medium transition-colors hover:text-primary',
                          pathname === item.href ? 'text-primary' : 'text-muted-foreground',
                        )}
                      >
                        {item.name}
                      </Link>
                    ))}
                  <Link href="/cart">
                    <Button variant="ghost" size="sm" className="relative">
                      <ShoppingCart className="h-4 w-4" />
                      {itemCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {itemCount > 99 ? '99+' : itemCount}
                        </span>
                      )}
                      <span className="hidden sm:inline ml-2">Cart</span>
                    </Button>
                  </Link>
                </>
              ) : null}
            </nav>
          </div>

          {/* Account Button */}
          {user ? (
            <div className="hidden md:flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <UserIcon className="h-4 w-4 mr-2" />
                    {user.email}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link href="/users">
                        <UserIcon className="h-4 w-4 mr-2" />
                        All Users
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden md:flex">
              <Link href="/auth">
                <Button variant="default" size="sm">
                  Start Now
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile menu button / Start Now */}
          <div className="md:hidden">
            {user ? (
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            ) : (
              <Link href="/auth">
                <Button variant="default" size="sm">
                  Start Now
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {user ? (
                <>
                  {userNavigation
                    .filter((item) => !item.adminOnly || isAdmin)
                    .map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className={cn(
                          'block px-3 py-2 text-base font-medium transition-colors',
                          pathname === item.href
                            ? 'text-primary bg-muted'
                            : 'text-muted-foreground hover:text-primary hover:bg-muted',
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  <Link
                    href="/cart"
                    className={cn(
                      'flex items-center px-3 py-2 text-base font-medium transition-colors',
                      pathname === '/cart'
                        ? 'text-primary bg-muted'
                        : 'text-muted-foreground hover:text-primary hover:bg-muted',
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Cart
                    {itemCount > 0 && (
                      <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {itemCount > 99 ? '99+' : itemCount}
                      </span>
                    )}
                  </Link>
                  <button
                    type="button"
                    className="flex items-center w-full px-3 py-2 text-base font-medium transition-colors text-muted-foreground hover:text-primary hover:bg-muted"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link href="/auth">
                  <Button variant="default" size="sm" className="relative">
                    Start Now
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
